import { Injectable, OnDestroy } from "@angular/core";
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from "@angular/fire/firestore";
import "firebase/firestore";
import * as moment from "moment";

import { environment } from "../../environments/environment";
import { User } from "../@core/data/users";
import { UserService } from "../@core/services/user.service";
import Shop from "../models/shop.model";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs/internal/Subject";
import { AngularFireFunctions } from "@angular/fire/functions";
import { convertTimestamp } from "../@core/utils/convertTimestamps.service";

@Injectable({
  providedIn: "root",
})
export class ShopService implements OnDestroy {
  visibility = environment.visibility;
  userLogin: User;
  private dbPath = "/shops";

  collRef: AngularFirestoreCollection<Shop>;
  shop: Shop;
  destroy$: Subject<void> = new Subject<void>();

  constructor(
    private db: AngularFirestore,
    private userStore: UserService,
    private fns: AngularFireFunctions
  ) {
    this.collRef = db.collection(this.dbPath);

    this.userStore
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: User) => {
        if (user) {
          this.userLogin = user;
          this.visibility = user.companyId
            ? user.companyId
            : environment.visibility;

          this.get(this.userLogin.shopId)
            .pipe(takeUntil(this.destroy$))
            .subscribe((res) => {
              this.shop = res;
              this.shop.createdOn = convertTimestamp(res.createdOn);
              this.shop.updatedOn = convertTimestamp(res.updatedOn);
              console.log("🚀 ~ ShopService ~ .subscribe ~ shop:", this.shop);
            });
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get(id: string) {
    return this.collRef.doc(id).valueChanges();
  }

  //CRUD Shop
  getShops(): AngularFirestoreCollection<Shop> {
    return this.db.collection(this.dbPath, (ref) =>
      ref.where("isDeleted", "==", false).orderBy("updatedOn", "desc")
    );
  }

  create(shop: Shop): any {
    shop.code = shop.code ? shop.code : "";
    shop.product = shop.product ? shop.product : false;
    shop.customer = shop.customer ? shop.customer : false;
    shop.warehouse = shop.warehouse ? shop.warehouse : false;
    shop.finance = shop.finance ? shop.finance : false;
    shop.receipt = shop.receipt ? shop.receipt : false;
    shop.purchase = shop.purchase ? shop.purchase : false;
    shop.loan = shop.loan ? shop.loan : false;
    shop.working = shop.working ? shop.working : false;
    shop.trade = shop.trade ? shop.trade : false;
    shop.receiptPurchase = shop.receiptPurchase ? shop.receiptPurchase : false;

    shop.isPremium = shop.isPremium ? shop.isPremium : false;
    shop.expDate = shop.expDate
      ? shop.expDate
      : moment().add(1, "months").endOf("day").toDate();

    shop.isArchived = shop.isArchived ? shop.isArchived : false;
    shop.shopIdArchived = shop.shopIdArchived ? shop.shopIdArchived : "";

    shop.isActive = true;
    shop.isDeleted = false;
    shop.createdOn = new Date();
    shop.createdBy = this.userLogin.uid;
    shop.updatedOn = new Date();
    shop.updatedBy = this.userLogin.uid;
    shop.visibility = this.visibility;
    shop.timestamp = new Date().getTime();

    console.log("create shop:", shop);
    return this.collRef.add({ ...shop }).then((docRef) => {
      shop.id = docRef.id;
      return shop;
    });
  }

  update(shop: Shop): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    return this.collRef.doc(shop.id).update({
      code: shop.code ? shop.code : "",
      title: shop.title ? shop.title : "",
      description: shop.description ? shop.description : "",
      coverUrl: shop.coverUrl ? shop.coverUrl : "",
      address: shop.address ? shop.address : "",
      mobile: shop.mobile ? shop.mobile : "",
      facebook: shop.facebook ? shop.facebook : "",
      youtube: shop.youtube ? shop.youtube : "",
      website: shop.website ? shop.website : "",
      email: shop.email ? shop.email : "",
      tax: shop.tax ? shop.tax : "",
      tags: shop.tags ? shop.tags : [],
      product: shop.product ? shop.product : false,
      customer: shop.customer ? shop.customer : false,
      warehouse: shop.warehouse ? shop.warehouse : false,
      finance: shop.finance ? shop.finance : false,
      receipt: shop.receipt ? shop.receipt : false,
      purchase: shop.purchase ? shop.purchase : false,
      loan: shop.loan ? shop.loan : false,
      working: shop.working ? shop.working : false,
      trade: shop.trade ? shop.trade : false,
      receiptPurchase: shop.receiptPurchase ? shop.receiptPurchase : false,
      isPremium: shop.isPremium ? shop.isPremium : false,
      expDate: shop.expDate
        ? shop.expDate
        : moment().add(1, "months").endOf("day").toDate(),

      isArchived: shop.isArchived ? shop.isArchived : false,
      shopIdArchived: shop.shopIdArchived ? shop.shopIdArchived : "",

      updatedOn: updatedOn,
      updatedBy: updatedBy,
      timestamp: new Date().getTime(),
    });
  }

  updateAvatar(id: string, coverUrl: string): Promise<void> {
    const updatedOn = new Date();
    return this.collRef.doc(id).update({
      coverUrl: coverUrl,
      updatedOn: updatedOn,
      timestamp: new Date().getTime(),
    });
  }

  delete(id: string): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    return this.collRef.doc(id).update({
      isDeleted: true,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      timestamp: new Date().getTime(),
    });
  }

  syncData(fromShopId: string, toShopId: string, mode: string) {
    const callable = this.fns.httpsCallable("api-systemConfig-syncData");
    return callable({
      fromShopId: fromShopId,
      toShopId: toShopId,
      mode: mode,
    });
  }
}
